import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import WorksheetRunCheckbox from 'notebook/worksheets/components/WorksheetRunCheckbox';
import {
  StatusTypeToDisplayNameMapping,
  Worksheet,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  WORKSHEET_TRANSITION_RUN_STATUS_STARTED_EVENT,
  composeWorksheetTransitionRunEventMetadata,
  WORKSHEET_TRANSITION_RUN_STATUS_ENDED_EVENT,
} from 'performance/processWorksheetsEvents';
import {updateProcedureMethodExecutionInstances} from 'procedures/network/ProcedureMethodExecutionInstanceDAO';
import {ProcedureStepGroupType} from 'procedures/ProcedureDomainTypes';
import {
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode as MethodTask,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode as PMEI,
} from 'procedures/worksheets/__generated/PMEIsQuery';
import CommentBox from 'procedures/worksheets/CommentBox';
import {useMaterialOutputsConfirmationErrorBanner} from 'procedures/worksheets/MaterialOutputsConfirmationError';
import {getProcedureType} from 'procedures/worksheets/WorksheetUtils';
import {DefaultStatusDisplayName} from 'processes/ProcessConstants';
import {FONT_WEIGHT_PROXIMA_REGULAR, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import DatetimeField, {
  DATEPICKER_CUSTOM_INPUT_USER_EDIT_FORMAL_TIME_FORMATS,
  DATEPICKER_CUSTOM_INPUT_USER_EDIT_INFORMAL_TIME_FORMATS,
} from 'ui-library/inputs/DatetimeField';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Modal from 'ui-library/modals/Modal';
import {
  DATETIME_FORMATS,
  PRESENTATIONAL_LOCAL_DATETIME_FORMAT,
  PRESENTATIONAL_TIME_FORMAT,
} from 'util/DateConstants';
import {isValidTime} from 'util/DateUtil';
import {withLoadingScreen} from 'util/Promises';

const TIMESTAMP_TOOLTIP_TEXT = 'Defaults to the current time if left blank.';

export async function updateTaskStatus(
  submitOption: WorksheetTaskStatusType,
  shouldSkipSteps: boolean,
  taskDatas: Array<{
    pmeiId: string;
    taskId: string;
  }>,
  onTaskUpdate: () => Promise<void>,
  timestampUtcIsoformat: string,
  comment: string
): Promise<void> {
  const transitionToStatus = StatusTypeToDisplayNameMapping[submitOption];
  assert(taskDatas.length > 0, `Unable to update worksheet task`);

  const readyTransitions = shouldSkipSteps
    ? {
        readyStepTaskTransitions: [
          {
            transitionToStatus: DefaultStatusDisplayName.SKIPPED,
            stepGroupType:
              submitOption === WorksheetTaskStatusType.IN_PROGRESS
                ? ProcedureStepGroupType.PRE_RUN
                : ProcedureStepGroupType.RUN,
          },
        ],
      }
    : {};

  const updates = taskDatas.map((taskData) => ({
    id: taskData.pmeiId,
    actionOrigin: ActionOrigin.WORKSHEET,
    transition: {
      comment,
      transitionToStatus,
      timestampUtcIsoformat,
      ...readyTransitions,
    },
  }));
  await withLoadingScreen(updateProcedureMethodExecutionInstances(updates).then(() => onTaskUpdate()));
}

// This component only utilizes a subset of the WorksheetTaskStatusType enum
export type TransitionStatusType = Extract<
  WorksheetTaskStatusType,
  | WorksheetTaskStatusType.COMPLETED
  | WorksheetTaskStatusType.FAILED
  | WorksheetTaskStatusType.IN_PROGRESS
  | WorksheetTaskStatusType.SKIPPED
>;

const END_RUN_STATUS_TO_BUTTON_PROPS = {
  [WorksheetTaskStatusType.COMPLETED]: {
    text: 'Complete run',
    submit: true,
    checkboxText: 'Mark all pending Run steps as skipped',
  },
  [WorksheetTaskStatusType.FAILED]: {
    text: 'Fail run',
    submit: true,
    style: ButtonStyle.NEGATIVE,
    checkboxText: 'Mark all pending Run steps as skipped',
  },
  [WorksheetTaskStatusType.SKIPPED]: {
    text: 'Skip run',
    submit: true,
    style: ButtonStyle.NEGATIVE,
    checkboxText: 'Mark all pending Run steps as skipped',
  },
  [WorksheetTaskStatusType.IN_PROGRESS]: {
    text: 'Start run',
    submit: true,
    checkboxText: 'Mark all pending Pre-run steps as skipped',
  },
};

const END_RUN_STATUS_TO_TIME_LABEL = {
  [WorksheetTaskStatusType.COMPLETED]: 'Completion time',
  [WorksheetTaskStatusType.FAILED]: 'Fail time',
  [WorksheetTaskStatusType.SKIPPED]: 'Skip time',
  [WorksheetTaskStatusType.IN_PROGRESS]: 'Start time',
};

export interface TransitionRunModalProps {
  onRequestClose: () => void;
  name: string;
  currentUser: HasAvatar;
  transitionToStatus: TransitionStatusType;
  worksheetTaskStatus?: WorksheetTaskStatusType;
  worksheet: Worksheet | null;
  onTaskUpdate: () => Promise<void>;
  disableCompleteRunButton?: boolean;
  pmeisAndTasks: Array<{task: MethodTask; pmei: PMEI}>;
}

export default function TransitionRunModal({
  onRequestClose,
  currentUser,
  pmeisAndTasks,
  name,
  transitionToStatus,
  worksheetTaskStatus,
  worksheet,
  onTaskUpdate,
  disableCompleteRunButton = false,
}: TransitionRunModalProps): JSX.Element {
  const [comment, setComment] = useState('');
  const [shouldSkipSteps, setShouldSkipSteps] = useState(false);
  const [timestamp, setTimestamp] = useState<string | undefined>();
  const actionButtonProps = END_RUN_STATUS_TO_BUTTON_PROPS[transitionToStatus];
  const pmeis = pmeisAndTasks.map((value) => value.pmei);
  const materialOutputsConfirmationErrorBanner = useMaterialOutputsConfirmationErrorBanner({
    worksheet,
    transitionToStatus,
    pmeis,
  });

  const isTimestampValidOrEmpty = timestamp
    ? isValidTime({textValue: timestamp, validTimeFormats: DATETIME_FORMATS})
    : true;

  // The action button should be disabled if:
  // 1. The transition status is COMPLETED and the complete run button override is provided
  // 2. The timestamp is invalid
  const actionButtonDisabled =
    !isTimestampValidOrEmpty ||
    (transitionToStatus === WorksheetTaskStatusType.COMPLETED && disableCompleteRunButton);

  const updateTaskStatusCallback = useCallback(
    async (submitOption: WorksheetTaskStatusType, shouldSkipSteps: boolean) => {
      const taskDatas = pmeisAndTasks.map((value) => ({pmeiId: value.pmei.id, taskId: value.task.id}));
      await updateTaskStatus(
        submitOption,
        shouldSkipSteps,
        taskDatas,
        onTaskUpdate,
        (timestamp ? moment(timestamp, DATETIME_FORMATS) : moment(new Date())).toISOString(),
        comment
      );
    },
    [onTaskUpdate, timestamp, comment, pmeisAndTasks]
  );

  const onCommentChange = (e: {target: {value: string}}): void => {
    setComment(e.target.value);
  };

  return (
    <Modal
      title={actionButtonProps.text}
      onRequestClose={onRequestClose}
      onRequestSubmit={async () => {
        logProfilerTimingEvent(WORKSHEET_TRANSITION_RUN_STATUS_STARTED_EVENT);
        await updateTaskStatusCallback(transitionToStatus, shouldSkipSteps);
        onRequestClose();
        const metadata = composeWorksheetTransitionRunEventMetadata(
          pmeisAndTasks.map((value) => value.task),
          transitionToStatus,
          BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION,
          worksheetTaskStatus,
          getProcedureType(pmeis)
        );
        logProfilerTimingEvent(WORKSHEET_TRANSITION_RUN_STATUS_ENDED_EVENT, metadata);
      }}
      buttons={[{...actionButtonProps, disabled: actionButtonDisabled}]}
      mediumLarge={true}
      scrollable={false}
      expandHeight={false}
    >
      {(transitionToStatus !== WorksheetTaskStatusType.IN_PROGRESS ||
        materialOutputsConfirmationErrorBanner != null) && (
        // If action is disabled or transition status is IN_PROGRESS & output confirmations are missing, show
        // that error banner. Otherwise, render terminal status info banner.
        <div className="u-small-spacing-bottom">
          {materialOutputsConfirmationErrorBanner != null ? (
            materialOutputsConfirmationErrorBanner
          ) : (
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text="Run will be updated to a terminal status upon confirmation. You won’t be able to further modify the Run status or create additional outputs."
            />
          )}
        </div>
      )}
      <div className={css(styles.container)}>
        <div className={css(styles.inputSection)}>
          <span className={css(styles.label)}>Unit operation</span>
          <span className={css(styles.inputText)}>{name}</span>
        </div>
        <div className={css(styles.inputSection)}>
          <div>
            <span className={css(styles.label)}>{END_RUN_STATUS_TO_TIME_LABEL[transitionToStatus]}</span>
            <Tooltip text={TIMESTAMP_TOOLTIP_TEXT}>
              <BenchlingIcon icon="info-circle" className={css(styles.timestampInfoIcon)} />
            </Tooltip>
          </div>
          <DatetimeField
            className={css(styles.dateTimeField)}
            onChange={(newDateValue) => {
              setTimestamp(newDateValue);
            }}
            value={timestamp}
            labelStyle={LabelStyle.HIDDEN}
            placeholder={timestamp}
            autoFocus={false}
            validDatetimeInputFormats={DATETIME_FORMATS}
            datetimePresentationalFormat={PRESENTATIONAL_LOCAL_DATETIME_FORMAT}
            timeInputPlaceholder="hh=mm AM/PM"
            timeInformalInputFormats={DATEPICKER_CUSTOM_INPUT_USER_EDIT_INFORMAL_TIME_FORMATS}
            timeFormalInputFormats={DATEPICKER_CUSTOM_INPUT_USER_EDIT_FORMAL_TIME_FORMATS}
            timePresentationalFormat={PRESENTATIONAL_TIME_FORMAT}
            preventDefault={true}
          />
        </div>
        <div className={css(styles.inputSection)}>
          <span className={css(styles.label)}>User</span>
          <span className={css(styles.inputText)}>
            <Avatar entity={currentUser} size={AvatarSize.XXSMALL} /> {currentUser.name}
          </span>
        </div>
      </div>
      <div className={css(styles.commentSection)}>
        <CommentBox comment={comment} onChange={onCommentChange} />
      </div>
      <WorksheetRunCheckbox
        isSkipChecked={shouldSkipSteps}
        onChangeSkipCheckbox={(checkbox: boolean) => {
          setShouldSkipSteps(checkbox);
        }}
        checkboxLabel={actionButtonProps.checkboxText}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  commentSection: {
    width: '100%',
    marginBottom: '16px',
  },
  container: {
    display: 'flex',
    marginBottom: '16px',
    gap: 48,
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '33%',
  },
  dateTimeField: {
    width: '170px',
    marginTop: '5px',
  },
  label: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    lineHeight: '16px',
    marginBottom: '4px',
  },
  inputText: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: '16px',
    marginTop: '5px',
  },
  timestampInfoIcon: {
    marginLeft: '6px',
    color: GRAY_DARKER,
  },
});
